import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Clock, FileText, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import PageMeta from '../components/PageMeta';

const ApplicationMain = () => {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerid');
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      icon: FileText,
      title: 'Application Received',
      description: 'We have received your application and our team is reviewing it.',
      status: 'completed'
    },
    {
      icon: Clock,
      title: 'Under Review',
      description: 'Our legal experts are analyzing your requirements.',
      status: 'current'
    },
    {
      icon: Send,
      title: 'Processing',
      description: 'We will begin processing your trademark application.',
      status: 'pending'
    },
    {
      icon: CheckCircle,
      title: 'Completion',
      description: 'Your application will be filed with the USPTO.',
      status: 'pending'
    }
  ];

  useEffect(() => {
    // Simulate progress
    const timer = setInterval(() => {
      setCurrentStep((prev) => (prev < 3 ? prev + 1 : prev));
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-[#FDFEFE] to-white">
      <PageMeta
        title="Application Status - Track Your Filing | Trademark Vector"
        description="Track the real-time progress of your trademark application, from submission and legal review through USPTO filing."
      />
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          {/* Success Message */}
          <div className="text-center mb-12 space-y-6">
            <div className="w-24 h-24 bg-[#C0392B]/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-12 h-12 text-[#C0392B]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#2C3E50]">
              Application Submitted Successfully!
            </h1>
            <p className="text-xl text-[#7F8C8D]">
              Thank you for choosing Trademark Vector. Your application has been received and is being processed.
            </p>
            {customerId && (
              <div className="inline-block bg-white rounded-lg px-6 py-3 shadow-md border-2 border-[#C0392B]/20">
                <p className="text-sm text-[#7F8C8D] mb-1">Your Application ID</p>
                <p className="text-2xl font-bold text-[#C0392B]">{customerId}</p>
              </div>
            )}
          </div>

          {/* Process Steps */}
          <Card className="border-2 border-[#7F8C8D]/20 shadow-xl mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[#2C3E50] mb-8 text-center">
                Application Process
              </h2>
              <div className="space-y-6">
                {steps.map((step, index) => {
                  const Icon = step.icon;
                  const isCompleted = index <= currentStep;
                  const isCurrent = index === currentStep;
                  
                  return (
                    <div 
                      key={index}
                      className={`flex items-start space-x-4 p-4 rounded-lg transition-all duration-500 ${
                        isCurrent ? 'bg-[#C0392B]/5 border-2 border-[#C0392B]/30' : ''
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        isCompleted ? 'bg-[#C0392B] text-white' : 'bg-[#7F8C8D]/20 text-[#7F8C8D]'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-lg font-semibold mb-1 ${
                          isCompleted ? 'text-[#C0392B]' : 'text-[#7F8C8D]'
                        }`}>
                          {step.title}
                        </h3>
                        <p className="text-[#7F8C8D]">{step.description}</p>
                      </div>
                      {isCompleted && (
                        <CheckCircle className="w-6 h-6 text-[#C0392B]" />
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* What's Next */}
          <Card className="border-2 border-[#7F8C8D]/20">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-[#2C3E50] mb-6">What Happens Next?</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-[#C0392B] flex-shrink-0 mt-1" />
                  <p className="text-[#7F8C8D]">
                    Our expert team will review your application within 24 hours
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-[#C0392B] flex-shrink-0 mt-1" />
                  <p className="text-[#7F8C8D]">
                    You will receive a confirmation email with next steps
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-[#C0392B] flex-shrink-0 mt-1" />
                  <p className="text-[#7F8C8D]">
                    We'll contact you if we need any additional information
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-[#C0392B] flex-shrink-0 mt-1" />
                  <p className="text-[#7F8C8D]">
                    Your trademark application will be filed with the USPTO
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Support */}
          <div className="text-center mt-12">
            <p className="text-[#7F8C8D] mb-4">
              Have questions? Our support team is here to help.
            </p>
            <a 
              href="/contact"
              className="inline-block bg-[#C0392B] hover:bg-[#A93226] text-white px-8 py-3 rounded-md font-semibold transition-all duration-300 hover:shadow-lg"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationMain;
